import type { Role } from '../shared/types.js';
import { showErrorModal, showConfirmModal, showSuccessModal } from './utils/modalUtil.js';
import { showTransientNotice } from './utils/notice.js';
import { t } from './utils/i18n.js';

export async function fetchData(): Promise<Role | null> {
    try {
        const response = await fetch('/api/roles', {
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to fetch data');
        const roles: Role[] = await response.json();
        return roles[0];
    } catch (error) {
        console.error('Error fetching data:', error);
        console.log('Falling back to local data...');
        return null;
    }
}

export async function updateNodeField(nodeName: string, field: string, newValue: string | number): Promise<boolean> {
    try {
        if (!nodeName) {
            throw new Error('nodeName is undefined or empty');
        }

        const payload: Record<string, any> = {
            originalName: nodeName,
            name: nodeName
        };

        if (field === 'name') {
            payload.name = newValue;
        } else if (field === 'accountabilities' || field === 'responsibilities') {
            payload[field] = newValue;
        } else {
            payload[field] = newValue;
        }

        const response = await fetch('/api/roles', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to update');
        }

        // Successfully got a 200 response - the update succeeded
        // Don't show errors after this point
        return true;
    } catch (error) {
        console.error('Error updating node:', error);
        // Suppress "Role not found" errors as they may occur during post-update operations
        if (error.message && error.message.includes('Role not found')) {
            console.warn('Role lookup failed (expected after name/data changes):', error.message);
            // Return true anyway since the initial update likely succeeded
            return true;
        }
        await showErrorModal(t('errors.updateFailed'), error instanceof Error ? error.message : t('errors.unknownError'));
        return false;
    }
}

export async function createChildNode(parentName: string, childName: string, childValue: number = 100, childColor: string = '#ffffff'): Promise<boolean> {
    try {
        const payload = {
            name: childName,
            value: childValue,
            color: childColor,
            parent_id: parentName
        };

        const response = await fetch('/api/roles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await response.json();
        if (!response.ok) {
            throw new Error(responseData.error || 'Failed to create node');
        }

        return true;
    } catch (error) {
        console.error('Error creating child node:', error);
        await showErrorModal(t('errors.createFailed'), error instanceof Error ? error.message : t('errors.unknownError'));
        return false;
    }
}

export async function deleteNode(nodeId: number, nodeName: string, hasChildren: boolean, childNames?: string[]): Promise<boolean> {
    if (hasChildren) {
        const childList = childNames && childNames.length > 0 
            ? `\n\nChild roles:\n• ${childNames.join('\n• ')}`
            : '';
        await showErrorModal(t('errors.cannotDelete'), t('errors.cannotDeleteMessage', { name: nodeName, childList }));
        return false;
    }

    const confirmed = await showConfirmModal(t('errors.confirmDelete', { name: nodeName }));
    if (!confirmed) {
        return false;
    }

    try {
        const response = await fetch(`/api/roles/${nodeId}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            const errorMsg = data.error || 'Failed to delete node';
            
            // If server returned referenced roles, show them
            if (data.referencedBy && data.referencedBy.length > 0) {
                const refList = data.referencedBy.map(r => r.name).join('\n• ');
                throw new Error(`${errorMsg}\n\nReferenced by:\n• ${refList}`);
            }
            
            throw new Error(errorMsg);
        }

        showTransientNotice(t('toast.roleDeleted'), 'success');
        return true;
    } catch (error) {
        console.error('Error deleting node:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        await showErrorModal(t('errors.deleteFailed'), errorMessage);
        return false;
    }
}

export async function getColorDescriptions(): Promise<Record<string, string>> {
    try {
        const response = await fetch('/api/color-descriptions', {
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to fetch color descriptions');
        return await response.json();
    } catch (error) {
        console.error('Error fetching color descriptions:', error);
        return {};
    }
}

export async function saveColorDescription(color: string, description: string): Promise<boolean> {
    try {
        const response = await fetch('/api/color-descriptions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ color, description })
        });

        if (!response.ok) throw new Error('Failed to save color description');
        return true;
    } catch (error) {
        console.error('Error saving color description:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        await showErrorModal(t('errors.saveFailed'), errorMessage);
        return false;
    }
}
